import { FC, memo, useContext } from 'react'
import { ScreenContext } from 'react-components'
import { ListingSectionObject } from '../../../../../transports/common/listing-section-transport'
import { BreadcrumbListTransport } from '../../../../../models/common/breadcrumb-model'
import { SuitablesModel } from '../../../../../models/common/suitables-model'
import { Offer } from '../../../../../models/common/offers-model'
import GridCard from '../../../../common/grid-card'
import CompactCard from '../../../../common/compact-card'
import { GridCardSkeleton, CompactCardSkeleton } from '../../../../common/card-skeletons'
import { EmblaCarousel } from '../../../../common/embla-carousel'
import {
    CarouselWrapper,
    CarouselHeader,
    CarouselIcon,
    TitleContainer,
    Heading,
    HeadingPrimary,
    SubHeading,
    SkeletonRow,
    SlideInner,
    SliderContainer,
} from './styles'
import { getBasePath } from '../../../../../utils/miscellaneous-utils'
import { useTranslation } from '../../../../../hooks/locale'
import { cardEventCategories, pageTypeEventCategories } from '../../../../../services/analytics-service/categories'

const MIN_LISTINGS = 3
const TREASURE_ICON_SRC = getBasePath('/assets/images/traum-tags/tressure.png')

interface Props {
    listings: ListingSectionObject[]
    loading: boolean
    isGridView: boolean
    breadcrumbModel: BreadcrumbListTransport
    offerModel: Offer
    suitablesModel: SuitablesModel
}

const HiddenGemCarousel: FC<Props> = ({
    listings,
    loading,
    isGridView,
    breadcrumbModel,
    offerModel,
    suitablesModel,
}) => {
    const { isMobile, isTablet, isWideScreen } = useContext(ScreenContext)
    const { t } = useTranslation()

    if (loading) {
        const skeletonCount = isMobile ? 1 : isGridView ? 2 : isTablet ? 2 : 3
        return (
            <CarouselWrapper>
                <CarouselHeader>
                    <CarouselIcon src={TREASURE_ICON_SRC} alt="" />
                    <TitleContainer>
                        <Heading>
                            <HeadingPrimary>{t('hiddenGemCarousel.headingPrimary')}</HeadingPrimary>
                            {t('hiddenGemCarousel.headingSecondary')}
                        </Heading>
                        <SubHeading>{t('hiddenGemCarousel.subHeading')}</SubHeading>
                    </TitleContainer>
                </CarouselHeader>
                <SkeletonRow>
                    {Array.from({ length: skeletonCount }).map((_, i) => (
                        <SlideInner key={`hidden-gem-skeleton-${i}`}>
                            {isGridView ? <CompactCardSkeleton /> : <GridCardSkeleton />}
                        </SlideInner>
                    ))}
                </SkeletonRow>
            </CarouselWrapper>
        )
    }

    if (listings.length < MIN_LISTINGS) {
        return null
    }

    const visibleSlides = isGridView
        ? isMobile
            ? 1.1
            : 2.1
        : isMobile
          ? 1.1
          : isTablet
            ? 2.2
            : isWideScreen
              ? 2.8
              : 2.4

    const slidesToScroll = Math.floor(visibleSlides)

    return (
        <CarouselWrapper>
            <CarouselHeader>
                <CarouselIcon src={TREASURE_ICON_SRC} alt="" />
                <TitleContainer>
                    <Heading>
                        <HeadingPrimary>{t('hiddenGemCarousel.headingPrimary')}</HeadingPrimary>
                        {t('hiddenGemCarousel.headingSecondary')}
                    </Heading>
                    <SubHeading>{t('hiddenGemCarousel.subHeading')}</SubHeading>
                </TitleContainer>
            </CarouselHeader>
            <SliderContainer>
                <EmblaCarousel
                    slides={listings.map((listing, index) => (
                        <SlideInner key={listing.id}>
                            {isGridView ? (
                                <CompactCard
                                    index={index}
                                    listingObject={listing}
                                    pageName={pageTypeEventCategories.SEARCH_RESULT_LISTING_PAGE}
                                    sectionName={cardEventCategories.sections.HIDDEN_GEM_CAROUSEL}
                                    breadcrumbModel={breadcrumbModel}
                                    offerModel={offerModel}
                                    suitablesModel={suitablesModel}
                                />
                            ) : (
                                <GridCard
                                    index={index}
                                    listingObject={listing}
                                    displayPrice
                                    displayPriceInfo
                                    requestUrl=""
                                    isObjectOverview={false}
                                    pageName={pageTypeEventCategories.SEARCH_RESULT_LISTING_PAGE}
                                    sectionName={cardEventCategories.sections.HIDDEN_GEM_CAROUSEL}
                                    useCarousel={false}
                                    breadcrumbModel={breadcrumbModel}
                                    offerModel={offerModel}
                                    suitablesModel={suitablesModel}
                                    showFeatures
                                    showFeatureLabel={false}
                                />
                            )}
                        </SlideInner>
                    ))}
                    visibleSlides={visibleSlides}
                    slidesToScroll={slidesToScroll}
                    showNav
                    navVariant="top-right"
                    navPosition={{ desktopTop: '-3rem', desktopRight: '1rem', wideTop: '-3rem', wideRight: '1rem' }}
                    viewportPadding="0.1rem 0"
                />
            </SliderContainer>
        </CarouselWrapper>
    )
}

export default memo(HiddenGemCarousel)
